/**
 * 下单进度的四步，对应 useReservation 里四个**真实事件**，不是编出来的假进度：
 *   locking  createOrder 已发出，班次正在被锁
 *   created  createOrder 已返回，拿到 ticketId / tradeNo
 *   paying   bringUpPayment 已发出
 *   done     支付已调起 / 零元单直接成
 */
export type BookingStep = '' | 'locking' | 'created' | 'paying' | 'done'

/** 等待期间摆在屏幕中央的「我买的是这个」 */
export interface BookingSummary {
  /** 航线名 */
  title: string
  /** 景点名 · 打卡点名 */
  place: string
  /** 今天 / 明天 / 周三 / 09-22 —— 和卡片「日期」那格同一套说法 */
  day: string
  /** 12:40 */
  time: string
  currency: string
  price: string
}

let appLoadNum = 0
const appLoadTimer: null | NodeJS.Timeout = null

// ── 下单进度卡的最短停留 ────────────────────────────────────────────────
//
// 支付接口快的时候（尤其是 ¥0 航线走 PAY_ZERO、整段支付被跳过），卡片会
// 一闪而过，读起来像闪屏故障而不是反馈（Zack 2026-09-16 在生产上看到）。
//
// 1 秒这个数是按卡片自己的动画节奏定的，不是拍的：
//   m-booking-progress 入场 0.45s + 一次步进 0.26s ≈ 0.75s 才读得完一档
// 留一点余量就是 1 秒。
//
// ⚠️ 不要再往上加。这张卡在**支付主路径**上，多出来的时间是每一单都要付的；
// 2 秒是拿一个偶发的观感问题去换一个必然的变慢，不划算。
const BOOKING_MIN_VISIBLE = 1000
let bookingShownAt = 0
let bookingCloseTimer: ReturnType<typeof setTimeout> | null = null

// 上一次换步的时刻。两步之间的差就是那一段的真实耗时，记下来给进度条
// 当下一次的预期速度用（见 utils/bookingPace.ts 那段说明）。
let bookingStepAt = 0

export const useGlobalStore = defineStore('global', {
  state: () => ({
    temp: {} as Record<string, any>,
    appShowState: false as boolean,
    /**
     * 「航拍进行中」的接续条是不是正挂着。
     *
     * 给 home-flight-entry 用：那个浮窗本来就有一颗「继续航拍」，和接续条
     * 说的是同一件事（Zack 实机看到两个框同时出现）。接续条在的时候把它
     * 收起来，两者不同时出现。
     *
     * 只在接续条挂着的那几秒为真 —— 接续条走完会跳去航拍页，跳之前那颗
     * 「继续航拍」仍然是轮询间隙的兜底，不能整个删掉。
     */
    journeyResumeVisible: false as boolean,
    appLoad: { loading: false as boolean | string, timeout: 200 },
    // 下单 + 支付这条路的进度。
    //
    // 它**不控制遮罩的开关** —— 开关仍然是 appLoad（useReservation 里那几处
    // setAppLoad 一个字没动）。这里只决定「遮罩长什么样」：active 为真时
    // App.vue 渲染 m-booking-progress 顶替那个转圈。
    //
    // 这么分是刻意的：万一进度这套出问题，最坏结果是退回原来的转圈，
    // 下单和支付一步都不会受影响。
    booking: {
      active: false,
      step: '' as BookingStep,
      summary: {
        title: '',
        place: '',
        day: '',
        time: '',
        currency: '',
        price: '',
      } as BookingSummary,
    },
    platformInfo: {
      version: '',
      system: '',
      beWrapped: false,
      engine: '',
      supporter: '',
      shell: '',
      shellVs: '',
      networkType: '',
      systemVs: '',
      platform: '',
      engineVs: '',
      supporterVs: '',
      webp: true,
    },
  }),

  getters: {
    clientInfo({ platformInfo }) {
      const { platform, system, systemVs, shell, shellVs } = platformInfo
      return `${platform}_${system}_${systemVs}&${shell}_${shellVs}`
    },

    // 是否运行在微信小程序内
    inMPWeixin({ platformInfo }) {
      return platformInfo.shell === 'MP-WEIXIN'
    },

    inMPAli({ platformInfo }) {
      return platformInfo.shell === 'MP-ALIPAY'
    },

    inMPToutiao({ platformInfo }) {
      return platformInfo.shell === 'MP-TOUTIAO'
    },

    inMP({ platformInfo }) {
      // 判断是否在任意平台的小程序
      const mpPlatforms = ['MP-WEIXIN', 'MP-ALIPAY', 'MP-BAIDU', 'MP-TOUTIAO', 'MP-QQ', 'MP-KUAISHOU', 'MP-JD', 'MP-360']
      return mpPlatforms.includes(platformInfo.shell)
    },

    // 是否运行在微信浏览器内
    inWeixin({ platformInfo }) {
      return platformInfo.shell === 'Weixin'
    },

    // 是否鸿蒙系统
    isHarmonyOS({ platformInfo }) {
      return platformInfo.system === 'HarmonyOS'
    },

    // 是否安卓系统
    isAndroid({ platformInfo }) {
      return platformInfo.system === 'Android'
    },

    // 是否iOS系统
    isIOS({ platformInfo }) {
      return platformInfo.system.toLowerCase() === 'ios'
    },

    isOtherBrowser({ platformInfo }) {
      const mpPlatforms = ['MP-WEIXIN', 'MP-ALIPAY', 'MP-BAIDU', 'MP-TOUTIAO', 'MP-QQ', 'MP-KUAISHOU', 'MP-JD', 'MP-360']
      return platformInfo.shell !== 'Weixin' && !mpPlatforms.includes(platformInfo.shell)
    },
  },

  actions: {
    setAppLoad(
      appLoad: boolean | string,
      options?: {
        timeout?: number
      },
    ) {
      const { timeout } = (options = Object.assign({ timeout: 200 }, options))

      if (appLoad) {
        const newTitle = typeof appLoad === 'string' ? appLoad : ''
        if (appLoadNum === 0 || newTitle !== this.appLoad.loading) {
          this.appLoad.timeout = timeout
          this.appLoad.loading = appLoad
        }
        appLoadNum++
        return
      }

      if (appLoadNum === 0) return

      appLoadNum--
      if (appLoadNum) return

      // 下单进度卡在的话，保证它至少停够 BOOKING_MIN_VISIBLE（见上面）。
      //
      // 只延后**视觉上的关闭**：调用方那边该 return 的 return、该报错的报错，
      // 一行逻辑都没被挡住。所以最坏情况只是错误提示晚 1 秒出现，
      // 不会改变任何业务时序。
      const held = this.booking.active
        ? BOOKING_MIN_VISIBLE - (Date.now() - bookingShownAt)
        : 0

      const close = () => {
        bookingCloseTimer = null
        this.appLoad.loading = false
        // 遮罩收了，下单进度也一并清掉 —— 见 startBooking 上面那段注释。
        this.booking.active = false
        this.booking.step = ''
      }

      if (held > 0) {
        // 同一时刻只留一个待关闭的定时器
        if (bookingCloseTimer) clearTimeout(bookingCloseTimer)
        bookingCloseTimer = setTimeout(close, held)
        return
      }

      close()
    },

    setPlatformInfo(info: typeof this.platformInfo) {
      this.platformInfo = info
    },

    // ── 下单进度（只管展示，不管流程）───────────────────────
    //
    // 没有 endBooking 给外面调 —— 清理挂在 setAppLoad(false) 里（见上面）。
    // 这样 booking 状态**不可能比遮罩活得久**：useReservation 有七八条提前
    // return 的分支，靠人肉在每条后面补一句清理，早晚会漏一条，
    // 而漏掉的后果是下一次任何 setAppLoad(true) 都会错误地弹出下单进度。
    startBooking(summary: BookingSummary) {
      // 上一单的延迟关闭还没跑完就又开了一单：先把它取消，
      // 否则那个 timer 会在新的一单进行到一半时把遮罩收掉。
      if (bookingCloseTimer) {
        clearTimeout(bookingCloseTimer)
        bookingCloseTimer = null
      }
      bookingShownAt = Date.now()
      bookingStepAt = bookingShownAt
      this.booking.active = true
      this.booking.step = 'locking'
      this.booking.summary = summary
    },

    // 只往前推，不往回退 —— 免得某条分支重复调用把进度条拉回去。
    // 进度条倒退比它不动更让人不安。
    setBookingStep(step: BookingStep) {
      if (!this.booking.active) return
      const order: BookingStep[] = ['locking', 'created', 'paying', 'done']
      if (order.indexOf(step) <= order.indexOf(this.booking.step)) return

      // 记下刚走完那一段花了多久。只记可预测的两段：
      // paying 之后是用户在支付面板上的时间，长短由他决定，记了没意义。
      const now = Date.now()
      if (step === 'created') recordBookingPace('lock', now - bookingStepAt)
      else if (step === 'paying') recordBookingPace('pay', now - bookingStepAt)
      bookingStepAt = now

      this.booking.step = step
    },

    getWeixinRedirectUrl(url: string) {
      return `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${apiConfig.WX_APPID}&redirect_uri=${encodeURIComponent(url)}&response_type=code&scope=snsapi_userinfo&state=direct_login#wechat_redirect`
    },
    toWeixinMiniProgram() {
      window.location.href = `weixin://dl/business/?appid=wx98ca8dab2d472ac5&path=pages/index/index`
    },
    toAlipayMiniProgram() {
      window.location.href = `alipays://platformapi/startapp?appId=2021005146692264&page=pages/index/index`
    },
    setTemp(key: string, value: any) {
      this.temp[key] = value
    },
    removeTemp(key: string) {
      delete this.temp[key]
    },
    getSocialClientType() {
      if (this.inMPWeixin) {
        return SocialClientType.WXXCX;
      }
      if (this.inMPAli) {
        return SocialClientType.ALI;
      }
      if (this.inMPToutiao) {
        return SocialClientType.DOUYIN;
      }
      if (this.inWeixin) {
        return SocialClientType.WXMP;
      }
      if (this.isOtherBrowser) {
        return SocialClientType.WAP;
      }
      return SocialClientType.WXXCX;
    },
    async getAppShowState() {
      const [err, res] = await to(getAppShowState())
      if (err) return
      this.appShowState = res
    },
  },
})
